import { api, apiBase } from './client';
import type { DeviceRow, DeviceDetail, Env, LifecycleData, LifecycleStatus } from '../types';

export function fetchDevices(params: URLSearchParams) {
  return api<DeviceRow[]>(`/devices?${params.toString()}`);
}

export function fetchDevice(sn: string, env?: Env) {
  const q = env ? `?env=${env}` : '';
  return api<DeviceDetail>(`/devices/${encodeURIComponent(sn)}${q}`);
}

export function fetchDeviceTypes() {
  return api<string[]>(`/devices/types`);
}

export interface Facets {
  types: string[];
  firmwares: string[];
  clients: string[];
}

export function fetchFacets(env: Env) {
  return api<Facets>(`/devices/facets?env=${env}`);
}

export function exportUrl(params: URLSearchParams): string {
  return `${apiBase()}/export/devices.csv?${params.toString()}`;
}

export interface TypeOption {
  typeId: number;
  typeName: string;
}

export function fetchTypeOptions(env: Env) {
  return api<TypeOption[]>(`/devices/type-options?env=${env}`);
}

/** Payload de creation (« Ajouter une borne »). Obligatoires : device_sn, device_name. */
export interface DeviceCreatePayload {
  device_sn: string;
  device_name: string;
  device_type_id?: number | null;
  device_status?: string;
  lifecycle_status?: LifecycleStatus;
  device_price?: number | null;
  device_latitude?: number | null;
  device_longitude?: number | null;
  hardware_version?: string | null;
  manufacturing_date?: string | null;
  /** Proprietaire (HEX users.user_uuid). Absent = compte stock ZCE. */
  owner_uuid?: string | null;
}

export function createDevice(env: Env, payload: DeviceCreatePayload) {
  return api<{ deviceId: number; deviceSn: string; env: Env }>(`/devices?env=${env}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface DevicePatch {
  device_name?: string;
  manufacturing_date?: string | null;
  hardware_version?: string | null;
  zce_serial_number?: string | null;
  client_name?: string | null;
  /** Appareils MQTT seulement (l'OCPP recoit son type de device-service). */
  device_type_id?: number | null;
}

export function updateDevice(sn: string, env: Env, patch: DevicePatch) {
  return api<null>(`/devices/${encodeURIComponent(sn)}?env=${env}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export interface OwnerRef {
  uuid: string | null;
  email: string | null;
}

export interface OwnerChangeResult {
  deviceId: number;
  from: OwnerRef;
  to: OwnerRef;
}

/** Changer le « Client » (proprietaire reel) d'une borne : transfert vers un compte existant. */
export function changeDeviceOwner(sn: string, env: Env, ownerUuid: string) {
  return api<OwnerChangeResult>(`/devices/${encodeURIComponent(sn)}/owner?env=${env}`, {
    method: 'POST',
    body: JSON.stringify({ owner_uuid: ownerUuid }),
  });
}

export function retireDevice(sn: string, env: Env) {
  return api<null>(`/devices/${encodeURIComponent(sn)}/retire?env=${env}`, { method: 'POST' });
}

export function deleteDevice(sn: string, env: Env) {
  return api<null>(`/devices/${encodeURIComponent(sn)}?env=${env}`, { method: 'DELETE' });
}

export interface DeletionImpact {
  deviceId: number;
  transactions: number;
  chargingTransactions: number;
  connectors: number;
  reservations: number;
  meterValues: number;
}

export function deviceDeletionImpact(sn: string, env: Env) {
  return api<DeletionImpact>(`/devices/${encodeURIComponent(sn)}/deletion-impact?env=${env}`);
}

/** FORCE delete: purge history then delete (admin). */
export function forceDeleteDevice(sn: string, env: Env) {
  return api<null>(`/devices/${encodeURIComponent(sn)}?env=${env}&force=true`, {
    method: 'DELETE',
  });
}

/** Fiche de vie: journal + matrice des transitions autorisees. */
export function fetchLifecycle(sn: string, env: Env) {
  return api<LifecycleData>(`/devices/${encodeURIComponent(sn)}/lifecycle-events?env=${env}`);
}

/** Transition du cycle de vie (operator+). 409 si la transition est interdite. */
export function transitionLifecycle(sn: string, env: Env, to: LifecycleStatus, reason?: string) {
  return api<{ from: LifecycleStatus; to: LifecycleStatus }>(
    `/devices/${encodeURIComponent(sn)}/lifecycle?env=${env}`,
    { method: 'POST', body: JSON.stringify({ to, reason }) }
  );
}
